import * as React from 'react';
import { useMemo } from 'react';
import { type IParsedFile } from '../../utils/download/useFileDownloadApiOptions.ts';

interface IDocumentsInlineViewerProvider {
  /** Verifica che l'app sia abilitata a visualizzare il drawer */
  isEnabled: boolean;
}
interface IDocumentsInlineViewerInfo {
  url: string;
  name: string;
  mimetype?: string;
}

export interface IDocumentsInlineViewerContext {
  handleSetCurrentDocument: (document: IParsedFile) => void;
  handleResetCurrentDocument: () => void;
  currentDocument: IDocumentsInlineViewerInfo | null;
  isEnabled: boolean;
}

export const DocumentsInlineViewerContext =
  React.createContext<IDocumentsInlineViewerContext>({
    handleSetCurrentDocument: () => {},
    handleResetCurrentDocument: () => {},
    currentDocument: null,
    isEnabled: false,
  });

export const useDocumentsInlineViewerContext = () =>
  React.useContext(DocumentsInlineViewerContext);

export function DocumentsInlineViewerContextProvider(
  props: React.PropsWithChildren<IDocumentsInlineViewerProvider>,
) {
  const { isEnabled } = props;
  const [currentDocument, setCurrentDocument] =
    React.useState<IDocumentsInlineViewerInfo | null>(null);

  const handleSetCurrentDocument = React.useCallback(
    (document: IParsedFile) => {
      // Revoca il vecchio Blob URL prima di generare uno nuovo
      if (currentDocument?.url) {
        URL.revokeObjectURL(currentDocument.url);
      }

      const fileBlobUrl = URL.createObjectURL(
        new Blob([document.data], { type: document.mimetype }),
      );

      setCurrentDocument({
        url: fileBlobUrl,
        name: document.filename,
        mimetype: document.mimetype,
      });
    },
    [currentDocument, setCurrentDocument],
  );

  const handleResetCurrentDocument = React.useCallback(() => {
    if (currentDocument?.url) {
      URL.revokeObjectURL(currentDocument.url);
    }
    setCurrentDocument(null);
  }, [currentDocument]);

  const contextValue = useMemo(
    () => ({
      currentDocument,
      handleSetCurrentDocument,
      handleResetCurrentDocument,
      isEnabled,
    }),
    [
      currentDocument,
      handleSetCurrentDocument,
      handleResetCurrentDocument,
      isEnabled,
    ],
  );

  return (
    <DocumentsInlineViewerContext.Provider value={contextValue}>
      {props.children}
    </DocumentsInlineViewerContext.Provider>
  );
}
